import React, { useState } from 'react'
import {
  validateConfirmPassword,
  validateDateOfBirth,
  validateEmail,
  validateGender,
  validateName,
  validatePassword,
  validatePhone,
  normalizeNationalPhoneDigits,
  getNationalPhoneLimits,
} from '../utils/validation'
import { useNavigate } from 'react-router-dom'
import { signInWebCustomer, signUpWebCustomer, reactivateAccount } from '../services/authService'
import { SIGNUP_VERIFY_EMAIL_KEY } from './VerifyEmailCode'
import { supabase } from '../config/supabase'
import CountryCodePicker from './CountryCodePicker'
import DateOfBirthPicker from './DateOfBirthPicker'
import { useAuth } from '../hooks/useAuth'
import { DEMO_CAMPAIGN_ENABLED, setDemoMode } from '../utils/demoMode'
import { clearDemoStore } from '../utils/demoStore'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'

const AuthModal = ({ mode, onClose, onSwitchMode, onAuth, onForgotPassword }) => {
  const navigate = useNavigate()
  const { signInWithGoogle, signInWithFacebook } = useAuth()
  useBodyScrollLock(true)
  const [loading, setLoading] = useState(false)
  const [oauthLoading, setOauthLoading] = useState({ google: false, facebook: false })
  const [serverError, setServerError] = useState('')
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    dateOfBirth: '',
    gender: ''
  })
  const [selectedCountry, setSelectedCountry] = useState({ code: '+1', country: 'United States/Canada' })
  const [errors, setErrors] = useState({})

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value
    })
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      })
    }
    if (serverError) {
      setServerError('')
    }
  }

  const handleEmailBlur = () => {
    const trimmed = formData.email.trim()
    if (trimmed !== formData.email) {
      setFormData((prev) => ({ ...prev, email: trimmed }))
    }
    const emailValidation = validateEmail(trimmed)
    setErrors((prev) => {
      if (emailValidation.isValid) {
        if (!prev.email) return prev
        const next = { ...prev }
        delete next.email
        return next
      }
      return { ...prev, email: emailValidation.message }
    })
  }

  const handleTryDemo = () => {
    if (!DEMO_CAMPAIGN_ENABLED) {
      setServerError('The campaign demo is currently turned off.')
      return
    }
    clearDemoStore()
    setDemoMode(true)
    onClose()
  }

  const validateForm = () => {
    const newErrors = {}

    // Email validation (both login and signup)
    const emailValidation = validateEmail(formData.email)
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.message
    }

    // Password validation (both login and signup)
    const passwordValidation = validatePassword(formData.password)
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.message
    }

    if (mode === 'signup') {
      // First Name validation
      const firstNameValidation = validateName(formData.firstName, 'First Name')
      if (!firstNameValidation.isValid) {
        newErrors.firstName = firstNameValidation.message
      }

      // Last Name validation
      const lastNameValidation = validateName(formData.lastName, 'Last Name')
      if (!lastNameValidation.isValid) {
        newErrors.lastName = lastNameValidation.message
      }

      // Country Code validation
      if (!selectedCountry || !selectedCountry.code) {
        newErrors.countryCode = 'Please select a country code'
      }

      // Phone validation (national number for selected country code)
      const phoneValidation = validatePhone(formData.phone, selectedCountry?.code)
      if (!phoneValidation.isValid) {
        newErrors.phone = phoneValidation.message
      }

      // Confirm Password validation
      const confirmPasswordValidation = validateConfirmPassword(
        formData.password,
        formData.confirmPassword
      )
      if (!confirmPasswordValidation.isValid) {
        newErrors.confirmPassword = confirmPasswordValidation.message
      }

      // Date of Birth validation (required, no minimum age)
      const dobValidation = validateDateOfBirth(formData.dateOfBirth, {
        minAge: 0,
        required: true,
      })
      if (!dobValidation.isValid) {
        newErrors.dateOfBirth = dobValidation.message
      }

      // Gender validation
      const genderValidation = validateGender(formData.gender)
      if (!genderValidation.isValid) {
        newErrors.gender = genderValidation.message
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setServerError('')
    
    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      if (mode === 'signup') {
        const cleanedPhone = normalizeNationalPhoneDigits(formData.phone || '', selectedCountry?.code)
        const countryCode = selectedCountry?.code || '+1'
        const fullMobile = `${countryCode}${cleanedPhone}`

        // Web customer signup
        const result = await signUpWebCustomer({
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          mobile: fullMobile,
          countryCode,
          dateOfBirth: formData.dateOfBirth,
          gender: formData.gender
        })

        if (!result.success && !result.alreadyRegistered) {
          setServerError(result.error || 'Failed to create account')
          setLoading(false)
          return
        }

        const email = formData.email.trim().toLowerCase()
        const profile = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          mobile: fullMobile,
          countryCode,
          dateOfBirth: formData.dateOfBirth,
          gender: formData.gender,
          email,
        }

        if (result.alreadyRegistered) {
          sessionStorage.setItem(SIGNUP_VERIFY_EMAIL_KEY, email)
          sessionStorage.setItem(`${SIGNUP_VERIFY_EMAIL_KEY}_profile`, JSON.stringify(profile))
          onClose()
          navigate('/verify-email', {
            state: {
              email,
              profile,
              emailResent: !!result.emailResent,
              notice: result.emailResent
                ? 'We sent a new 6-digit code to your email.'
                : 'If you did not receive a code, tap Resend code below.',
            },
          })
          setLoading(false)
          return
        }

        if (result.needsEmailConfirmation) {
          sessionStorage.setItem(SIGNUP_VERIFY_EMAIL_KEY, email)
          sessionStorage.setItem(`${SIGNUP_VERIFY_EMAIL_KEY}_profile`, JSON.stringify(profile))
          onClose()
          navigate('/verify-email', { state: { email, profile } })
          setLoading(false)
          return
        }

        if (onAuth) {
          onAuth({
            success: true,
            message: result.message || 'Account created successfully!'
          })
        }
        onClose()
      } else {
        // Web customer login
        const result = await signInWebCustomer(formData.email.trim().toLowerCase(), formData.password)

        if (!result.success) {
          // Check if account is inactive - show message to contact admin (same as OAuth users)
          if (result.isInactive) {
            // Show alert message to contact admin (same as OAuth users)
            alert(result.error || 'Your account has been deactivated. Please contact the administrator to reactivate your account.')
                setLoading(false)
                return
          } else if (result.isRoleBlocked) {
            // Role-based blocking - show specific error with alert
            setServerError(result.error)
            // Also show alert for better visibility
            alert(result.error || 'You do not have permission to access this area.')
          } else if (result.isEmailNotConfirmed) {
            const email = formData.email.trim().toLowerCase()
            sessionStorage.setItem(SIGNUP_VERIFY_EMAIL_KEY, email)
            onClose()
            navigate('/verify-email', { state: { email } })
            setLoading(false)
            return
          } else {
            setServerError(result.error || 'Invalid email or password')
          }
          setLoading(false)
          return
        }

        // Success
        if (onAuth) {
          onAuth({
            success: true,
            user: result.user,
            profile: result.profile
          })
        }
        onClose()
      }

      // Clear form
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
      dateOfBirth: '',
      gender: ''
    })
    setSelectedCountry({ code: '+1', country: 'United States/Canada' })
    setErrors({})
    } catch (error) {
      console.error('Auth error:', error)
      setServerError('An unexpected error occurred. Please try again.')
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setOauthLoading(prev => ({ ...prev, google: true }))
    setServerError('')
    
    try {
      // Ensure any in-progress booking draft is flushed before leaving the page
      window.dispatchEvent(new Event('flush-pending-booking-draft'))
      const result = await signInWithGoogle()
      if (result.success) {
        // OAuth redirects, so we don't need to handle success here
        // The callback will be handled by AuthContext
      } else {
        setServerError(result.error || 'Failed to sign in with Google')
        setOauthLoading(prev => ({ ...prev, google: false }))
      }
    } catch (error) {
      console.error('Google login error:', error)
      setServerError('An error occurred during Google sign in. Please try again.')
      setOauthLoading(prev => ({ ...prev, google: false }))
    }
  }

  const handleFacebookLogin = async () => {
    setOauthLoading(prev => ({ ...prev, facebook: true }))
    setServerError('')
    
    try {
      window.dispatchEvent(new Event('flush-pending-booking-draft'))
      const result = await signInWithFacebook()
      if (result.success) {
        // OAuth redirects, so we don't need to handle success here
        // The callback will be handled by AuthContext
      } else {
        setServerError(result.error || 'Failed to sign in with Facebook')
        setOauthLoading(prev => ({ ...prev, facebook: false }))
      }
    } catch (error) {
      console.error('Facebook login error:', error)
      setServerError('An error occurred during Facebook sign in. Please try again.')
      setOauthLoading(prev => ({ ...prev, facebook: false }))
    }
  }

  const isLogin = mode === 'login'

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-3 sm:p-4 overscroll-none"
    >
      <div
        className={`bg-white rounded-xl w-full max-h-[90vh] overflow-y-auto overscroll-contain ${
          isLogin
            ? 'max-w-md p-5 sm:p-6'
            : 'max-w-lg p-4 sm:p-5'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex justify-between items-start ${isLogin ? 'mb-3' : 'mb-3'}`}>
          <div className="flex-1">
            <h2 className={`${isLogin ? 'text-xl sm:text-2xl' : 'text-xl sm:text-2xl'} font-bold`}>
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            {isLogin && (
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">Sign in to continue booking your appointments</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 ml-4 shrink-0">
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {serverError && (
          <div className={`${isLogin ? 'mb-2 p-2' : 'mb-4 p-3'} bg-red-50 border border-red-200 rounded-lg`}>
            <p className="text-red-600 text-sm">{serverError}</p>
          </div>
        )}

        {/* Social Login Buttons */}
        <div className={`${isLogin ? 'mb-3 space-y-2' : 'mb-3 space-y-2'}`}>
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading || oauthLoading.google || oauthLoading.facebook}
            className={`w-full flex items-center justify-center gap-3 px-4 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-gray-700 ${
              isLogin ? 'py-2.5 text-sm' : 'py-2 text-sm'
            }`}
          >
            {oauthLoading.google ? (
              <i className="fas fa-spinner fa-spin text-blue-600"></i>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            <span>Continue with Google</span>
          </button>

          {isLogin && DEMO_CAMPAIGN_ENABLED && (
            <button
              type="button"
              onClick={handleTryDemo}
              disabled={loading || oauthLoading.google || oauthLoading.facebook}
              className="w-full rounded-lg border-2 border-amber-400 bg-amber-50 py-2.5 text-sm font-semibold text-amber-900 transition hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Explore freely — nothing is saved to the real database."
            >
              <i className="fas fa-flask mr-2" aria-hidden="true"></i>
              Try Demo
            </button>
          )}

          {/* <button
            type="button"
            onClick={handleFacebookLogin}
            disabled={loading || oauthLoading.google || oauthLoading.facebook}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-gray-700"
          >
            {oauthLoading.facebook ? (
              <i className="fas fa-spinner fa-spin text-blue-600"></i>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            )}
            <span>Continue with Facebook</span>
          </button> */}
        </div>

        {/* Divider */}
        <div className={`relative ${isLogin ? 'mb-3' : 'mb-3'}`}>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500 text-xs sm:text-sm">Or continue with email</span>
          </div>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className={isLogin ? 'space-y-2.5' : 'space-y-2.5'}>
            {mode === 'signup' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      First Name<span className="text-red-500"> *</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      aria-required="true"
                      placeholder="First name"
                      disabled={loading}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
                        errors.firstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Last Name<span className="text-red-500"> *</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      aria-required="true"
                      placeholder="Last name"
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent ${
                        errors.lastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Mobile Number<span className="text-red-500"> *</span>
                  </label>
          <div className={`flex w-full items-stretch rounded-lg border bg-white overflow-hidden divide-x divide-gray-300 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent ${
            errors.phone || errors.countryCode ? 'border-red-500' : 'border-gray-300'
          }`}>
                      <CountryCodePicker
                        selectedCountry={selectedCountry}
                        onCountrySelect={(country) => {
                          setSelectedCountry(country)
                          setFormData((prev) => ({
                            ...prev,
                            phone: normalizeNationalPhoneDigits(prev.phone, country?.code),
                          }))
                          if (errors.countryCode) {
                            setErrors(prev => {
                              const newErrors = { ...prev }
                              delete newErrors.countryCode
                              return newErrors
                            })
                          }
                        }}
                        embedded
                        error={!!errors.countryCode}
                      />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => {
                          const { name } = e.target
                          const digitsOnly = normalizeNationalPhoneDigits(
                            e.target.value,
                            selectedCountry?.code,
                          )

                          setFormData({
                            ...formData,
                            [name]: digitsOnly
                          })

                          if (errors[name]) {
                            setErrors({
                              ...errors,
                              [name]: ''
                            })
                          }

                          if (serverError) {
                            setServerError('')
                          }
                        }}
                        onBlur={() => {
                          const phoneValidation = validatePhone(formData.phone, selectedCountry?.code)
                          if (!phoneValidation.isValid) {
                            setErrors((prev) => ({ ...prev, phone: phoneValidation.message }))
                          }
                        }}
                        required
                        placeholder="Enter your mobile number"
                        maxLength={getNationalPhoneLimits(selectedCountry?.code).max}
                        inputMode="numeric"
                        autoComplete="tel-national"
                        className={`flex-1 min-w-0 px-3 py-2 text-sm border-0 bg-transparent focus:ring-0 focus:outline-none ${
                          errors.phone ? 'bg-red-50' : ''
                        }`}
                      />
                  </div>
                  {errors.countryCode && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.countryCode}</p>
                  )}
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.phone}</p>
                  )}
                </div>
              </>
            )}
            
            <div>
              <label className={`block text-xs ${isLogin ? 'mb-1 font-bold' : 'mb-1 font-medium'} text-gray-700`}>
                Email<span className="text-red-500"> *</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                required
                aria-required="true"
                autoComplete="email"
                inputMode="email"
                placeholder="Enter your email"
                className={`w-full px-3 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent ${
                  isLogin ? 'py-2.5' : 'py-2'
                } ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
            
            <div>
              <label className={`block text-xs ${isLogin ? 'mb-1 font-bold' : 'mb-1 font-medium'} text-gray-700`}>
                Password<span className="text-red-500"> *</span>
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                aria-required="true"
                placeholder="Enter your password"
                className={`w-full px-3 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent ${
                  isLogin ? 'py-2.5' : 'py-2'
                } ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>
            
            {mode === 'signup' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Confirm Password<span className="text-red-500"> *</span>
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    placeholder="Confirm your password"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.confirmPassword}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <DateOfBirthPicker
                    id="signup-dateOfBirth"
                    label="Date of Birth"
                    value={formData.dateOfBirth}
                    onChange={(nextValue) => {
                      setFormData((prev) => ({ ...prev, dateOfBirth: nextValue }))
                      if (errors.dateOfBirth) {
                        setErrors((prev) => {
                          const next = { ...prev }
                          delete next.dateOfBirth
                          return next
                        })
                      }
                    }}
                    error={errors.dateOfBirth}
                    required
                    compact
                  />

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Gender<span className="text-red-500"> *</span>
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      required
                      aria-required="true"
                      className={`w-full h-[42px] px-3 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                        errors.gender ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select gender</option>
                      <option value="Female">Female</option>
                      <option value="Male">Male</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                    {errors.gender && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.gender}</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          
          <button
            type="submit"
            disabled={loading || oauthLoading.google || oauthLoading.facebook}
            className={`w-full bg-secondary text-white rounded-lg hover:bg-gray-800 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
              isLogin
                ? 'py-2.5 mt-3 text-sm sm:text-base'
                : 'py-2.5 mt-3 text-sm'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <i className="fas fa-spinner fa-spin mr-2"></i>
                {isLogin ? 'Signing In...' : 'Creating Account...'}
              </span>
            ) : (
              isLogin ? 'Sign In' : 'Sign Up'
            )}
          </button>
        </form>
        
        {isLogin && (
          <div className="mt-2.5 text-center">
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-primary hover:underline text-sm"
            >
              Forgot Password?
            </button>
          </div>
        )}
        
        <div className={`${isLogin ? 'mt-3' : 'mt-3'} text-center`}>
          {isLogin ? (
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={onSwitchMode}
                className="text-primary font-bold hover:underline"
              >
                Sign Up
              </button>
            </p>
          ) : (
            <button
              onClick={onSwitchMode}
              className="text-primary hover:underline text-sm sm:text-base"
            >
              Already have an account? Sign In
            </button>
          )}
        </div>
      </div>

    </div>
  )
}

export default AuthModal

